/**
 * The signature layer's shared plumbing (DESIGN.md "Motion" and "The
 * signature layer"). Every signature animation goes through `sigAnimate`, and
 * every duration and curve is read from the `--sig-*` tokens in
 * packages/tokens/src/tokens.css, so the CSS stays the one source.
 */

/** The reader asked for reduced motion: every signature is instant (The Reduced Motion Rule). */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** A signature moves only when the engine can animate and the reader has not asked for stillness. */
export function signatureMoves(): boolean {
  return (
    typeof Element !== 'undefined' &&
    typeof Element.prototype.animate === 'function' &&
    !prefersReducedMotion()
  )
}

const tokens = new Map<string, string>()
let themeObserver: MutationObserver | undefined
let observedTheme = ''

function watchTheme() {
  if (themeObserver || typeof document === 'undefined' || typeof MutationObserver === 'undefined')
    return
  observedTheme = document.documentElement.className
  themeObserver = new MutationObserver(() => tokens.clear())
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
}

export function sigVar(name: string): string {
  watchTheme()
  const currentTheme = document.documentElement.className
  if (currentTheme !== observedTheme) {
    observedTheme = currentTheme
    tokens.clear()
  }
  const known = tokens.get(name)
  if (known !== undefined) return known
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  tokens.set(name, value)
  return value
}

/** A `--sig-*` duration in milliseconds; the fallback covers a page without the tokens. */
export function sigMs(name: string, fallback: number): number {
  const value = sigVar(name)
  if (value.endsWith('ms')) return Number.parseFloat(value)
  if (value.endsWith('s')) return Number.parseFloat(value) * 1000
  return fallback
}

export function sigNumber(name: string, fallback: number): number {
  const value = Number.parseFloat(sigVar(name))
  return Number.isFinite(value) ? value : fallback
}

/** A curve token, or the exponential ease-out where `linear()` is not supported. */
export function sigEase(name: string): string {
  const value = sigVar(name)
  if (value && typeof CSS !== 'undefined' && CSS.supports('animation-timing-function', value)) {
    return value
  }
  return 'cubic-bezier(0.22, 1, 0.36, 1)'
}

/**
 * Every signature animation. If the browser draws no frames (a hidden tab, a
 * busy machine), a timer finishes it, so nothing stays covered, hidden or
 * half drawn (The Complete-at-Rest Rule). A PAUSED animation is being
 * inspected through the Web Animations API and is left alone.
 */
export function sigAnimate(
  el: Element,
  frames: Keyframe[] | PropertyIndexedKeyframes,
  options: KeyframeAnimationOptions & { duration: number },
): Animation {
  const animation = el.animate(frames, options)
  if (options.iterations !== Number.POSITIVE_INFINITY) {
    const total = options.duration + (options.delay ?? 0)
    window.setTimeout(() => {
      if (animation.playState === 'running') animation.finish()
    }, total + 120)
  }
  return animation
}

/** Run `done` once the animation ends, however it ends. */
export function afterAnimation(animation: Animation, done: () => void) {
  animation.finished.then(done, done)
}

/** The mask property family this engine takes: unprefixed, or WebKit's. */
export const MASK: 'mask' | 'webkitMask' =
  typeof CSS !== 'undefined' &&
  CSS.supports?.('mask-image', 'linear-gradient(transparent, transparent)')
    ? 'mask'
    : 'webkitMask'

export interface MaskLayers {
  Image: string
  Size: string
  Repeat: string
  Position: string
}

/** Set (or clear, with null) an inline mask in this engine's property family. */
export function setMask(el: HTMLElement, mask: MaskLayers | null) {
  const style = el.style as unknown as Record<string, string>
  for (const key of ['Image', 'Size', 'Repeat', 'Position'] as const) {
    style[MASK + key] = mask ? mask[key] : ''
  }
}

/** The keyframe property that moves a mask, in this engine's family. */
export const MASK_POSITION = `${MASK}Position`

/**
 * The pen's curve: an even hand that slows into the end of the line,
 * f(t) = (1 - 2^(-k t)) / (1 - 2^-k), sampled into 24 linear keyframes.
 */
export function penCurve<T>(frame: (progress: number) => T): T[] {
  const k = sigNumber('--sig-pen-k', 3)
  const steps = 24
  return Array.from({ length: steps + 1 }, (_, i) =>
    frame((1 - 2 ** ((-k * i) / steps)) / (1 - 2 ** -k)),
  )
}

/** Whether `el` reads right to left. */
export function readsRtl(el: Element): boolean {
  return getComputedStyle(el).direction === 'rtl'
}

/** The colour a cover must match: the first opaque background up the tree. */
export function paperOf(el: Element): string {
  for (let node: Element | null = el; node && node !== document.documentElement; ) {
    const background = getComputedStyle(node).backgroundColor
    if (background && background !== 'transparent' && !/rgba\(.*,\s*0\)$/.test(background)) {
      return background
    }
    node = node.parentElement
  }
  return getComputedStyle(document.body).backgroundColor
}
